/**
 * FeatureGate Component
 * 
 * Standardized component for conditionally rendering content based on feature flags.
 * This replaces FeatureWrapper and provides a consistent API.
 */

import React from 'react'
import { useFeatureFlag } from '../../shared/hooks/useFeatureFlag'
import { FeatureFlagName } from '../../lib/types/featureFlags.types'

interface FeatureGateProps {
  /**
   * The feature flag name to check
   */
  feature: FeatureFlagName
  
  /**
   * Optional tenant ID (defaults to user's tenant)
   */
  tenantId?: string
  
  /**
   * Content to render when feature is enabled
   */
  children: React.ReactNode
  
  /**
   * Content to render when feature is disabled
   */
  fallback?: React.ReactNode
  
  /**
   * Content to render while checking feature flag
   */
  loading?: React.ReactNode
  
  /**
   * Whether to show nothing when disabled (instead of fallback)
   */
  hideWhenDisabled?: boolean
}

/**
 * FeatureGate - Conditionally render content based on feature flags
 * 
 * @example
 * ```tsx
 * <FeatureGate feature="curriculum_builder">
 *   <CurriculumBuilder />
 * </FeatureGate>
 * 
 * <FeatureGate 
 *   feature="advanced_analytics" 
 *   fallback={<UpgradePrompt />}
 * >
 *   <AnalyticsDashboard />
 * </FeatureGate>
 * ```
 */
export default function FeatureGate({
  feature,
  tenantId,
  children,
  fallback = null,
  loading = null,
  hideWhenDisabled = false
}: FeatureGateProps) {
  const { isEnabled, loading: isLoading } = useFeatureFlag(feature, { tenantId })

  if (isLoading) {
    return <>{loading}</>
  }

  if (!isEnabled) {
    if (hideWhenDisabled) {
      return null
    }
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * Higher-order component to wrap components with feature flag check
 * 
 * @example
 * ```tsx
 * const CurriculumBuilder = withFeatureGate(
 *   CurriculumBuilderComponent,
 *   'curriculum_builder'
 * )
 * ```
 */
export function withFeatureGate<P extends object>(
  Component: React.ComponentType<P>,
  feature: FeatureFlagName,
  options?: {
    tenantId?: string
    fallback?: React.ComponentType<P>
  }
) {
  return function FeatureGatedComponent(props: P) {
    return (
      <FeatureGate
        feature={feature}
        tenantId={options?.tenantId}
        fallback={options?.fallback ? <options.fallback {...props} /> : null}
      >
        <Component {...props} />
      </FeatureGate>
    )
  }
}
